/**
 * Manage native video playback across the page:
 * - only one video plays at a time
 * - pause videos when they leave the viewport
 * - pause slider videos when their slide is no longer active
 */
const videoPlayback = (() => {
	const videos = new Set();
	const VIEWPORT_THRESHOLD = 0.1;

	let observer = null;

	const isFullscreen = (video) => {
		const fullscreenElement =
			document.fullscreenElement || document.webkitFullscreenElement;

		if (fullscreenElement && video.contains(fullscreenElement)) {
			return true;
		}

		return Boolean(video.webkitDisplayingFullscreen);
	};

	const pauseVideo = (video) => {
		if (video.paused || isFullscreen(video)) {
			return;
		}

		video.pause();
	};

	const pauseAllExcept = (activeVideo) => {
		videos.forEach((video) => {
			if (video !== activeVideo) {
				pauseVideo(video);
			}
		});
	};

	const onPlay = (event) => {
		pauseAllExcept(event.currentTarget);
	};

	const onIntersection = (entries) => {
		entries.forEach(({ target, isIntersecting }) => {
			if (!isIntersecting) {
				pauseVideo(target);
			}
		});
	};

	const getObserver = () => {
		if (!observer) {
			observer = new IntersectionObserver(onIntersection, {
				threshold: VIEWPORT_THRESHOLD,
			});
		}

		return observer;
	};

	const register = (video) => {
		if (!(video instanceof HTMLVideoElement) || videos.has(video)) {
			return;
		}

		videos.add(video);
		getObserver().observe(video);
		video.addEventListener("play", onPlay);
	};

	const registerAll = (root = document) => {
		root.querySelectorAll("video").forEach(register);
	};

	const bindSwiper = (swiper) => {
		if (!swiper?.el?.querySelector("video")) {
			return;
		}

		registerAll(swiper.el);

		const pauseInactiveSlides = () => {
			swiper.slides.forEach((slide) => {
				if (slide.classList.contains("swiper-slide-active")) {
					return;
				}

				slide.querySelectorAll("video").forEach(pauseVideo);
			});
		};

		swiper.on("slideChangeTransitionStart", pauseInactiveSlides);
	};

	const init = () => {
		registerAll();
	};

	return {
		init,
		register,
		bindSwiper,
	};
})();

export default videoPlayback;
